import type { TFunction } from 'i18next';

import { roomTypesEN, roomTypesFR } from '../../i18n/roomTypes';
import type { DemandDimension, DemandRow, SeriesPoint, ShopLabel } from '../../common/hooks/use-brand-api.hook';

/** The API's keys for the two rows that are not a value of the dimension. */
export const OTHER_KEY = '__other__';
export const UNKNOWN_KEY = '__unknown__';

/** A country's name in the reader's language; the API sends ISO alpha-2 where it knows it. */
export function countryName(code: string | null | undefined, lang: string): string {
    if (!code) return '';
    if (!/^[A-Z]{2}$/.test(code)) return code;
    try {
        return new Intl.DisplayNames([lang], { type: 'region' }).of(code) ?? code;
    } catch {
        return code;
    }
}

/** A closed-vocabulary value (appearance, colour family, material, finish) in the reader's language. */
export function vocab(t: TFunction, value: string | null | undefined): string {
    if (!value) return t('BRAND_PORTAL.COMMON.NOT_KNOWN');
    return t(`BRAND_PORTAL.VOCAB.${value.replace(/-/g, '_').toUpperCase()}`, { defaultValue: humanize(value) });
}

export function humanize(value: string): string {
    const s = value.replace(/[_-]+/g, ' ').trim();
    return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * A room type as the visualizer reported it ("Living Room", the scene
 * classifier's label): the common rooms from our own words, the rest from the
 * app's Places365 table where it has the language, else as written.
 */
export function roomTypeName(value: string, lang: string, t?: TFunction): string {
    const human = humanize(value).replace(/\b\w/g, (c) => c.toUpperCase());
    const key = human.replace(/\s+/g, '_').toUpperCase();
    const fallback = lang === 'fr' ? roomTypesFR[human] ?? roomTypesFR[value] ?? human : roomTypesEN[human] ?? roomTypesEN[value] ?? human;
    return t ? t(`BRAND_PORTAL.ROOM.${key}`, { defaultValue: fallback }) : fallback;
}

/** How a shop is shown: its name when it agreed to be listed, otherwise "A retailer in France". */
export function shopName(t: TFunction, label: ShopLabel, lang: string): string {
    if (label.kind === 'listed') return label.name;
    return label.country ? t('BRAND_PORTAL.SHOP.ANONYMOUS_IN', { country: countryName(label.country, lang) }) : t('BRAND_PORTAL.SHOP.ANONYMOUS');
}

/** The words for one demand row in the dimension it belongs to. */
export function rowLabel(t: TFunction, dim: DemandDimension, row: Pick<DemandRow, 'key' | 'label' | 'country' | 'city' | 'folded'>, lang: string): string {
    if (row.key === OTHER_KEY) return t('BRAND_PORTAL.COMMON.OTHER');
    if (row.key === UNKNOWN_KEY) return t('BRAND_PORTAL.COMMON.NOT_KNOWN');
    switch (dim) {
        case 'country':
            return countryName(row.key, lang);
        case 'city':
            return row.country ? `${row.city ?? row.label}, ${countryName(row.country, lang)}` : row.city ?? row.label;
        case 'appearance':
        case 'colorFamily':
            return vocab(t, row.key);
        case 'roomType':
            return roomTypeName(row.key, lang, t);
        case 'week':
            return weekLabel(row.key, lang);
        default:
            return row.label;
    }
}

/** "2026-W42" → "12 Oct" (the Monday of that ISO week). */
export function weekLabel(key: string, lang: string): string {
    const m = /^(\d{4})-W(\d{2})$/.exec(key);
    if (!m) return key;
    const year = Number(m[1]);
    const week = Number(m[2]);
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const monday = new Date(jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * 86_400_000 + (week - 1) * 7 * 86_400_000);
    return monday.toLocaleDateString(lang, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function dayLabel(key: string, lang: string): string {
    if (/^\d{4}-W\d{2}$/.test(key)) return weekLabel(key, lang);
    const d = new Date(`${key}T00:00:00.000Z`);
    return Number.isNaN(d.getTime()) ? key : d.toLocaleDateString(lang, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * The API's series as the trend chart wants it. A bucket the API withheld
 * (fewer than three shoppers) stays null: the chart shows a gap, never a zero.
 */
export function trendPoints(points: SeriesPoint[], lang: string) {
    return points.map((p) => ({ label: dayLabel(p.key, lang), value: p.visualizations as number, previous: p.previous }));
}

export function signed(n: number, digits = 0): string {
    const v = digits ? n.toFixed(digits) : String(Math.round(n));
    return n > 0 ? `+${v}` : v;
}

/** Download a CSV the API produced. */
export function saveCsv(buffer: ArrayBuffer, name: string) {
    const url = URL.createObjectURL(new Blob([buffer], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
